"use client";

import React from "react";
import { ArrowRight, CircleCheck, Loader2, Dog, Mail, User, Phone, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
function ContactForm() {
  const {
    handleSubmit,
    isSubmitting,
    isSubmitted,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="flex flex-col items-start gap-4 p-6 bg-accent/50 border-l-4 border-primary rounded-sm animate-in fade-in slide-in-from-bottom-2 duration-500">
        <div className="flex items-center gap-2 text-primary font-semibold">
          <CircleCheck className="size-6" />
          <span className="text-xl font-serif">Request Received</span>
        </div>
        <p className="text-muted-foreground">
          Thank you for reaching out. Jessica will review your dog's needs and contact you shortly to discuss a restorative plan.
        </p>
        <Button variant="outline" onClick={() => resetForm()} className="rounded-sm border-primary/20 hover:bg-primary/5">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <FormField control={control} name="full_name" rules={{
      required: "Your name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Your Name</FormLabel>
            <FormControl>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input placeholder="Jessica Gade" className="pl-10 rounded-sm bg-background" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Email Address</FormLabel>
            <FormControl>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input type="email" placeholder="jessica@example.com" className="pl-10 rounded-sm bg-background" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="organization" rules={{
      required: "Dog's name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Dog's Name</FormLabel>
            <FormControl>
              <div className="relative">
                <Dog className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input placeholder="Buddy" className="pl-10 rounded-sm bg-background" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="role" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Breed</FormLabel>
            <FormControl>
              <Input placeholder="Golden Retriever" className="rounded-sm bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Phone Number</FormLabel>
            <FormControl>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input type="tel" placeholder="+1 (970) 555-0123" className="pl-10 rounded-sm bg-background" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_interest" rules={{
      required: "Please select a primary concern"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Primary Concern</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="rounded-sm bg-background w-full">
                  <div className="flex items-center gap-2">
                    <Activity className="size-4 text-muted-foreground" />
                    <SelectValue placeholder="Select concern" />
                  </div>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="mobility">Mobility Support</SelectItem>
                <SelectItem value="recovery">Post-Hike Recovery</SelectItem>
                <SelectItem value="senior">Senior Dog Comfort</SelectItem>
                <SelectItem value="performance">Performance Support</SelectItem>
                <SelectItem value="relaxation">Relaxation & Stress</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <div className="md:col-span-2 pt-4">
        <Button type="submit" disabled={isSubmitting} size="lg" className="bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-sm h-14 px-8 text-base tracking-wide shadow-md hover:shadow-lg transition-all duration-300 group w-full sm:w-auto">
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-5 animate-spin" />
              Processing...
            </> : <>
              Start the Healing
              <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ModernWindowSolutions() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section data-section-id="227563" id="trail-tested-mobility-for-your-best-friend" className="relative bg-background text-foreground min-h-[100svh] w-full overflow-hidden" ref={sectionRef}>
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[100svh] w-full">
        <div className="flex flex-col justify-center items-center lg:items-end px-6 py-24 lg:py-12 z-10 order-2 lg:order-1">
          <div className={`w-full max-w-2xl lg:pr-12 xl:pr-16 transition-all duration-1000 ease-out ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-foreground font-semibold tracking-tight leading-[1.1] mb-6">
              Restorative,
              <br />
              Movement.
            </h1>
            <p className="font-default text-lg md:text-xl text-muted-foreground mb-10 leading-relaxed max-w-xl font-normal">
              Clinical Bodywork for the Durango Dog. Specialized mobility support 
              and restorative recovery for your active and aging companions.
            </p>
            
            <div className="space-y-8">
              <div className="bg-accent/30 p-8 rounded-sm border border-border/50 shadow-sm">
                <h2 className="font-serif text-2xl mb-6 text-foreground">Let's Start the Healing - Contact Form</h2>
                <FormProvider formId="start_healing_form_contact" sectionName="ModernWindowSolutions" defaultValues={{
                full_name: "",
                email: "",
                organization: "",
                role: "",
                phone: "",
                service_interest: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                organization: {
                  required: true
                },
                service_interest: {
                  required: true
                }
              }} submitText="Start the Healing" successMessage="Thank you! Jessica will be in touch soon.">
                  <ContactForm />
                </FormProvider>
              </div>

              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <CircleCheck className="size-4 text-primary" />
                  <span>Clinical Care</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <CircleCheck className="size-4 text-primary" />
                  <span>DPT Led</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <CircleCheck className="size-4 text-primary" />
                  <span>Trail-Tested</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative h-[50vh] lg:h-auto w-full overflow-hidden order-1 lg:order-2 bg-muted" ref={imageRef}>
          <img src="https://durangodogmassage.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-19-at-21.09.52-2.jpeg" alt="Clinical dog massage and bodywork session" className={`absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[2000ms] ease-out ${isImageVisible ? "scale-100" : "scale-110"}`} />
          <div className="absolute inset-0 bg-gradient-to-r from-background/40 to-transparent lg:hidden" />
        </div>
      </div>
    </section>;
}